
import React from 'react';

const number = (value, digits = 3) =>
  Number(value ?? 0).toLocaleString('en-IN', {
    maximumFractionDigits: digits,
  });

const money = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(Number(value ?? 0));

const formatDate = (value) => {
  if (!value) return '—';

  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);

  return Number.isNaN(parsed.getTime())
    ? '—'
    : parsed.toLocaleDateString('en-IN');
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'in_stock':
      return 'In Stock';
    case 'low_stock':
      return 'Low Stock';
    case 'out_of_stock':
      return 'Out of Stock';
    default:
      return status || 'Unknown';
  }
};

const StockTable = ({
  stock = [],
  loading = false,
  expandedProductId,
  stockDetails = {},
  loadingProductId,
  onToggleDetails,
}) => {
  if (loading) {
    return (
      <div className="stock-state">
        Loading stock records...
      </div>
    );
  }

  if (!stock.length) {
    return (
      <div className="stock-state">
        <h3>No stock records found</h3>
        <p>Try changing your search or stock status filter.</p>
      </div>
    );
  }

  return (
    <div className="stock-table-wrapper">
      <table className="stock-table">
        <thead>
          <tr>
            <th>Product</th>
            <th>SKU</th>
            <th>Brand</th>
            <th>Unit</th>
            <th>Available Quantity</th>
            <th>Low Stock Alert</th>
            <th>Status</th>
            <th>Last Updated</th>
            <th>Details</th>
          </tr>
        </thead>

        <tbody>
          {stock.map((item) => {
            const productId = String(item.product_id);
            const isExpanded =
              expandedProductId === productId;
            const details = stockDetails[productId];
            const isLoadingDetails =
              loadingProductId === productId;

            return (
              <React.Fragment key={productId}>
                <tr>
                  <td>
                    <strong>{item.product_name || '—'}</strong>
                  </td>

                  <td>{item.sku || '—'}</td>
                  <td>{item.brand || '—'}</td>
                  <td>{item.unit || '—'}</td>

                  <td className="stock-quantity">
                    {number(item.quantity)} {item.unit || ''}
                  </td>

                  <td>
                    {number(item.low_stock_alert)}{' '}
                    {item.unit || ''}
                  </td>

                  <td>
                    <span
                      className={`stock-status stock-status-${item.stock_status}`}
                    >
                      {getStatusLabel(item.stock_status)}
                    </span>
                  </td>

                  <td>
                    {item.updated_at
                      ? new Date(item.updated_at).toLocaleString(
                          'en-IN',
                          {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          }
                        )
                      : '—'}
                  </td>

                  <td>
                    <button
                      type="button"
                      className="stock-details-button"
                      onClick={() => onToggleDetails(productId)}
                      disabled={isLoadingDetails}
                      aria-expanded={isExpanded}
                    >
                      {isLoadingDetails
                        ? 'Loading...'
                        : isExpanded
                          ? 'Hide Batches'
                          : 'View Batches'}
                    </button>
                  </td>
                </tr>

                {isExpanded && (
                  <tr className="stock-expanded-row">
                    <td colSpan={9}>
                      {isLoadingDetails ? (
                        <p>Loading batch details...</p>
                      ) : details?.error ? (
                        <p className="stock-inline-error">
                          {details.error}
                        </p>
                      ) : (
                        <div className="stock-batches-panel">
                          <div className="stock-batches-heading">
                            <div>
                              <h3>
                                {details?.product_name ||
                                  item.product_name}{' '}
                                — Batch Details
                              </h3>
                              <p>
                                Remaining quantities for individual
                                batches.
                              </p>
                            </div>

                            <div className="stock-batch-total">
                              Total stock:{' '}
                              <strong>
                                {number(
                                  details?.quantity ??
                                    item.quantity
                                )}{' '}
                                {details?.unit || item.unit || ''}
                              </strong>
                            </div>
                          </div>

                          {!details?.batches?.length ? (
                            <p>
                              No batches found for this product.
                            </p>
                          ) : (
                            <div className="stock-batch-table-wrapper">
                              <table className="stock-batch-table">
                                <thead>
                                  <tr>
                                    <th>Batch Number</th>
                                    <th>Purchase Date</th>
                                    <th>Expiry Date</th>
                                    <th>Received Qty</th>
                                    <th>Remaining Qty</th>
                                    <th>Purchase Price</th>
                                    <th>Selling Price</th>
                                    <th>GST</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {details.batches.map((batch) => (
                                    <tr key={batch.batch_id}>
                                      <td>
                                        <strong>
                                          {batch.batch_number ||
                                            '—'}
                                        </strong>
                                      </td>

                                      <td>
                                        {formatDate(
                                          batch.purchase_date
                                        )}
                                      </td>

                                      <td>
                                        {formatDate(
                                          batch.expiry_date
                                        )}
                                      </td>

                                      <td>
                                        {number(
                                          batch.quantity_received
                                        )}{' '}
                                        {details.unit || item.unit || ''}
                                      </td>

                                      <td>
                                        {number(
                                          batch.quantity_remaining
                                        )}{' '}
                                        {details.unit || item.unit || ''}
                                      </td>

                                      <td>
                                        {money(batch.purchase_price)}
                                      </td>

                                      <td>
                                        {money(batch.selling_price)}
                                      </td>

                                      <td>
                                        {number(batch.gst_rate, 2)}%
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default StockTable;
